//-- Copyright (c) 2021 TMT Insights Group, LLC
import { NavLinks } from '@/__configs/config_types';
import { UserProfile } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Drawer, Grid, IconButton, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { selectUser } from '../../redux/selectors/authSelectors';
import PolarisIcon from '../_common/PolarisIcon';
import DropdownAccordion from '../_common/DropdownAccordion';
import { useAppSelector } from '@/redux/hooks';

type SidebarMobileProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  navLinks: NavLinks;
};
const SidebarMobile = ({ open, setOpen, navLinks }: SidebarMobileProps) => {
  const user: UserProfile = useAppSelector(selectUser);
  return (
    <DrawerMenu open={open} onClose={() => setOpen(false)} anchor="right">
      <CloseIconBox>
        <IconButton edge="start" size="large" sx={{ mt: -1, mb: -1, ml: 1 }} onClick={() => setOpen(false)}>
          <PolarisIcon icon="close" height={25} width={25} />
        </IconButton>
      </CloseIconBox>
      <UserInfo>
        <Typography variant="subtitle2" noWrap>
          {user.first_name} {user.last_name}
        </Typography>
        <Typography variant="caption">{user.email}</Typography>
        {!!user.customer_associations?.length && (
          <Typography variant="subtitle1">{user.customer_associations.map((customer) => customer.name).join(' • ')}</Typography>
        )}
      </UserInfo>
      <NavList>
        {navLinks.map((navLink) =>
          navLink.to ? (
            <NavLink key={`navlink-${navLink.id}`} exact={navLink.exact} to={navLink.to}>
              {navLink.label}
            </NavLink>
          ) : (
            <DropdownAccordion key={`navlink-${navLink.id}`} label={navLink.label} items={navLink.children} />
          )
        )}
      </NavList>
    </DrawerMenu>
  );
};

export default SidebarMobile;

const DrawerMenu = styled(Drawer)`
  & .MuiDrawer-paper {
    background: ${({ theme }) => theme.palette.background.default};
    width: 60%;
    max-width: 350px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }
`;

const CloseIconBox = styled(Grid)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(0.5)};
  display: flex;
  justify-content: flex-end;
`;

const UserInfo = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  line-height: 1;
  margin: ${({ theme }) => theme.spacing(0, 4)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 1.1rem;
  border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};

  & .MuiTypography-caption {
    word-break: break-all;
    font-size: 0.9rem;
  }

  & .MuiTypography-subtitle1 {
    font-size: 0.88rem;
  }
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(3, 0, 0, 0)};
  margin: 0;

  & a,
  & button {
    position: relative;
    padding: ${({ theme }) => theme.spacing(1, 0, 1, 4)};
    opacity: 0.7;
    font-size: 1.1rem;
    text-decoration: none;
    transition: ease opacity 600ms;
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    color: ${({ theme }) => theme.palette.text.primary};

    &:hover,
    &.active {
      opacity: 1;
      transition: ease opacity 600ms;
      color: ${({ theme }) => theme.palette.primary.main};
    }

    &.active {
      color: ${({ theme }) => theme.palette.primary.main};
      opacity: 1;
    }
  }
`;
